import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Puff } from 'react-loader-spinner'
import { BiTrash, BiStar } from 'react-icons/bi'
import { getAchievPoints, deleteAchievPoint } from '../../actions/achievPoints'
import { getAchievements } from '../../actions/achievements'
import { getEventLevels } from '../../actions/event_levels'
import Button from '../../components/Button'
import Table from '../../components/Table'
import Confirmation from '../../components/Confirmation/Confirmation'
import AchievementPointsModal from './Modal'
import styles from './styles.module.scss'

const TABLE_HEADERS = [
  { key: 'achievement', label: 'Вид достижения' },
  { key: 'level', label: 'Уровень' },
  { key: 'points', label: 'Баллы' },
  { key: 'actions', label: '', width: '60px' }
]

const AchievementsPoints = () => {
  const dispatch = useDispatch()

  const { list, loading } = useSelector(state => state.achiev_points)
  const { list: achievements } = useSelector(state => state.achievements)
  const { list: eventLevels } = useSelector(state => state.event_levels)

  const [selectedItem, setSelectedItem] = useState(null)
  const [confirmation, setConfirmation] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [sortField, setSortField] = useState('achievement')
  const [sortAsc, setSortAsc] = useState(true)

  useEffect(() => {
    dispatch(getAchievements())
    dispatch(getEventLevels())
    dispatch(getAchievPoints())
  }, [dispatch])

  const achievementDict = useMemo(() =>
    achievements?.reduce((acc, cur) => ({ ...acc, [cur.id]: cur.name }), {}) || {},
    [achievements]
  )

  const eventLevelDict = useMemo(() =>
    eventLevels?.reduce((acc, cur) => ({ ...acc, [cur.news_level_id]: cur.news_level }), {}) || {},
    [eventLevels]
  )

  const handleDelete = useCallback((id) => (e) => {
    e.stopPropagation()
    setConfirmation(id)
  }, [])

  const openModal = useCallback((data) => () => {
    setSelectedItem(data || {})
  }, [])

  const closeModal = useCallback(() => {
    setSelectedItem(null)
  }, [])

  const acceptConfirmation = useCallback(async () => {
    setIsDeleting(true)
    try {
      await dispatch(deleteAchievPoint(confirmation))
    } finally {
      setConfirmation(null)
      setIsDeleting(false)
    }
  }, [dispatch, confirmation])

  const rejectConfirmation = useCallback(() => {
    setConfirmation(null)
  }, [])

  const isModalOpen = selectedItem !== null
  const isAddMode = selectedItem && !selectedItem.rrowid

  const handleSort = useCallback((field) => {
    if (sortField === field) setSortAsc(v => !v)
    else { setSortField(field); setSortAsc(true) }
  }, [sortField])

  const sortedList = useMemo(() => {
    if (!list) return []
    return [...list].sort((a, b) => {
      if (sortField === 'achievement') {
        const aVal = achievementDict[a.rachid] || ''
        const bVal = achievementDict[b.rachid] || ''
        return sortAsc ? aVal.localeCompare(bVal, 'ru') : bVal.localeCompare(aVal, 'ru')
      }
      if (sortField === 'level') {
        const aVal = eventLevelDict[a.rlevelid] || ''
        const bVal = eventLevelDict[b.rlevelid] || ''
        return sortAsc ? aVal.localeCompare(bVal, 'ru') : bVal.localeCompare(aVal, 'ru')
      }
      const aVal = a.achpoints ?? 0
      const bVal = b.achpoints ?? 0
      return sortAsc ? aVal - bVal : bVal - aVal
    })
  }, [list, achievementDict, eventLevelDict, sortField, sortAsc])

  const renderEmptyState = () => (
    <div className={styles.emptyState}>
      <BiStar size={64} className={styles.emptyIcon} />
      <p>Нет добавленных баллов за достижения</p>
      <Button onClick={openModal()}>Добавить первую запись</Button>
    </div>
  )

  const renderLoading = () => (
    <div className={styles.loadingState}>
      <Puff
        height={50}
        width={50}
        color="#3689FF"
        ariaLabel="Загрузка..."
      />
      <span>Загрузка данных...</span>
    </div>
  )

  const renderTable = () => (
    <>
      <Table className={styles.table}>
        <thead>
          <tr>
            {TABLE_HEADERS.map(({ key, label, width }) => (
              <th
                key={key}
                style={{ ...(width ? { width } : {}), ...(key !== 'actions' ? { cursor: 'pointer' } : {}) }}
                onClick={key !== 'actions' ? () => handleSort(key) : undefined}
              >{label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedList.map((item) => (
            <tr
              key={item.rrowid}
              onClick={openModal(item)}
              className={styles.tableRow}
            >
              <td>
                <span className={styles.achievementName}>
                  {achievementDict[item.rachid] || '—'}
                </span>
              </td>
              <td>
                <span className={styles.levelBadge}>
                  {eventLevelDict[item.rlevelid] || '—'}
                </span>
              </td>
              <td>
                <span className={styles.pointsValue}>{item.achpoints}</span>
              </td>
              <td>
                <button
                  type="button"
                  className={styles.deleteBtn}
                  onClick={handleDelete(item.rrowid)}
                  title="Удалить запись"
                  aria-label="Удалить"
                >
                  <BiTrash size={20} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <div className={styles.actions}>
        <Button onClick={openModal()}>Добавить запись</Button>
        <span className={styles.counter}>
          Всего записей: {sortedList.length}
        </span>
      </div>
    </>
  )

  return (
    <div className={styles.wrapper}>
      <header className={styles.header}>
        <h1>Баллы за достижения</h1>
      </header>

      <main className={styles.content}>
        {loading && renderLoading()}
        {!loading && sortedList.length === 0 && renderEmptyState()}
        {!loading && sortedList.length > 0 && renderTable()}
      </main>

      <AchievementPointsModal
        open={isModalOpen}
        onClose={closeModal}
        data={selectedItem}
        addMode={isAddMode}
        achievements={achievements}
        eventLevels={eventLevels}
      />

      {confirmation && (
        <Confirmation
          top="30px"
          left="40px"
          title="Уверены что хотите удалить запись?"
          acceptConfirmation={acceptConfirmation}
          rejectConfirmation={rejectConfirmation}
          loading={isDeleting}
        />
      )}
    </div>
  )
}

export default AchievementsPoints
